import React from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Divider,
} from '@mui/material';
import { palette } from '../theme/colors';
import { StockStatus } from '../hooks/useProductSearch';

export interface ProductFiltersProps {
  categories: string[];
  selectedCategory?: string;
  onCategoryChange: (category: string) => void;
  minPrice?: number;
  maxPrice?: number;
  onPriceChange: (min: number | undefined, max: number | undefined) => void;
  stockStatus?: StockStatus;
  onStockStatusChange: (status: StockStatus) => void;
}

interface FilterButtonProps {
  label: string;
  isActive: boolean;
  onClick: () => void;
}

const FilterButton: React.FC<FilterButtonProps> = ({ label, isActive, onClick }) => (
  <Button
    onClick={onClick}
    fullWidth
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      px: 2,
      py: 1.5,
      borderRadius: 2,
      border: `1.5px solid ${isActive ? palette.primary : palette.border}`,
      bgcolor: isActive ? `${palette.primary}15` : 'transparent',
      color: isActive ? palette.primary : palette.textPrimary,
      textTransform: 'none',
      fontWeight: isActive ? 600 : 500,
      fontSize: '0.875rem',
      transition: 'all 0.2s ease',
      '&:hover': {
        bgcolor: isActive ? `${palette.primary}20` : palette.sky,
        borderColor: palette.primary,
      },
    }}
  >
    {label}
  </Button>
);

export const ProductFilters: React.FC<ProductFiltersProps> = ({
  categories,
  selectedCategory,
  onCategoryChange,
  minPrice,
  maxPrice,
  onPriceChange,
  stockStatus = StockStatus.ALL,
  onStockStatusChange,
}) => {
  const [localMinPrice, setLocalMinPrice] = React.useState<string>(
    minPrice?.toString() || ''
  );
  const [localMaxPrice, setLocalMaxPrice] = React.useState<string>(
    maxPrice?.toString() || ''
  );

  const handleMinPriceChange = (value: string) => {
    setLocalMinPrice(value);
    const numValue = value === '' ? undefined : parseFloat(value);
    if (value === '' || !isNaN(numValue!)) {
      onPriceChange(numValue, maxPrice);
    }
  };

  const handleMaxPriceChange = (value: string) => {
    setLocalMaxPrice(value);
    const numValue = value === '' ? undefined : parseFloat(value);
    if (value === '' || !isNaN(numValue!)) {
      onPriceChange(minPrice, numValue);
    }
  };

  return (
    <Box
      sx={{
        bgcolor: palette.surface,
        borderRadius: 3,
        border: `1px solid ${palette.border}`,
        p: 3,
      }}
    >
      {/* Categories Filter */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="subtitle2"
          fontWeight={700}
          sx={{ mb: 2, color: palette.textPrimary }}
        >
          Category
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <FilterButton
            label="All Categories"
            isActive={!selectedCategory || selectedCategory === ''}
            onClick={() => onCategoryChange('')}
          />
          {categories.map((category) => (
            <FilterButton
              key={category}
              label={category}
              isActive={selectedCategory === category}
              onClick={() => onCategoryChange(category)}
            />
          ))}
        </Box>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Price Range Filter */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="subtitle2"
          fontWeight={700}
          sx={{ mb: 2, color: palette.textPrimary }}
        >
          Price Range (DZD)
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Min"
            value={localMinPrice}
            onChange={(e) => handleMinPriceChange(e.target.value)}
            type="number"
            InputProps={{
              inputProps: { min: 0, step: 10 },
            }}
            sx={{
              flex: 1,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                bgcolor: palette.sky,
                '& fieldset': {
                  borderColor: palette.border,
                },
                '&:hover fieldset': {
                  borderColor: palette.primary,
                },
                '&.Mui-focused fieldset': {
                  borderColor: palette.primary,
                },
              },
            }}
          />
          <Typography variant="body2" color={palette.textMuted}>
            to
          </Typography>
          <TextField
            size="small"
            placeholder="Max"
            value={localMaxPrice}
            onChange={(e) => handleMaxPriceChange(e.target.value)}
            type="number"
            InputProps={{
              inputProps: { min: 0, step: 10 },
            }}
            sx={{
              flex: 1,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                bgcolor: palette.sky,
                '& fieldset': {
                  borderColor: palette.border,
                },
                '&:hover fieldset': {
                  borderColor: palette.primary,
                },
                '&.Mui-focused fieldset': {
                  borderColor: palette.primary,
                },
              },
            }}
          />
        </Box>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Stock Status Filter */}
      <Box>
        <Typography
          variant="subtitle2"
          fontWeight={700}
          sx={{ mb: 2, color: palette.textPrimary }}
        >
          Stock Status
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <FilterButton
            label="All Stock"
            isActive={stockStatus === StockStatus.ALL}
            onClick={() => onStockStatusChange(StockStatus.ALL)}
          />
          <FilterButton
            label="In Stock"
            isActive={stockStatus === StockStatus.IN_STOCK}
            onClick={() => onStockStatusChange(StockStatus.IN_STOCK)}
          />
          <FilterButton
            label="Low Stock"
            isActive={stockStatus === StockStatus.LOW_STOCK}
            onClick={() => onStockStatusChange(StockStatus.LOW_STOCK)}
          />
          <FilterButton
            label="Out of Stock"
            isActive={stockStatus === StockStatus.OUT_OF_STOCK}
            onClick={() => onStockStatusChange(StockStatus.OUT_OF_STOCK)}
          />
        </Box>
      </Box>
    </Box>
  );
};
